import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { QueryClientProvider } from '@tanstack/react-query'

// Import the generated route tree
import { routeTree } from './routeTree.gen'

// Instrument Sans is the UI face; 700 is only used for large page titles.
import '@fontsource/instrument-sans/latin-400.css'
import '@fontsource/instrument-sans/latin-500.css'
import '@fontsource/instrument-sans/latin-600.css'
import '@fontsource/instrument-sans/latin-700.css'
// Only the weights `font-mono` is actually paired with (regular + medium).
import '@fontsource/jetbrains-mono/latin-400.css'
import '@fontsource/jetbrains-mono/latin-500.css'
import './styles.css'
import reportWebVitals from './reportWebVitals.ts'
import { enableBigIntSerialization } from './lib/utils/bigintSerializer'
import { AuthProvider } from './lib/auth/AuthProvider'
import { restoreFromIndexedDB } from './lib/auth/authTokenBackup'
import { cleanupLegacyAuthStorage } from './lib/auth/legacyAuthCleanup'
import {
  deleteAllRxDBDatabases,
  resetAllLocalData,
  resetRxDB,
} from './lib/db/resetUtils'
import { installCrossTabLogoutListener } from './lib/db/rxdb/utils/crossTabLogoutListener'
import { queryClient } from './lib/api/queryClient'

// Initialize i18n
import './lib/i18n'

// Initialize monitoring
import { initializeSentry } from './lib/monitoring/sentry'
import { logger } from './lib/monitoring/logger'
import { LogCategory } from './lib/monitoring/types'
import { ErrorBoundary } from './components/app/ErrorBoundary'
import { reportBuildPairToSentry } from './lib/build/buildInfo'
import { initTheme } from './lib/theme/theme'

// Apply light/dark before anything renders to avoid a theme flash.
initTheme()

// Initialize Sentry error tracking
initializeSentry()

// Fire-and-forget: tag every subsequent Sentry event with the API build
// the client is talking to. No await — never block boot on telemetry.
void reportBuildPairToSentry()

// Enable BigInt serialization to prevent JSON.stringify errors
enableBigIntSerialization()

// React to logout in another tab before that tab deletes the shared IDB.
installCrossTabLogoutListener()

// Expose reset utilities globally for development/debugging
if (import.meta.env.DEV) {
  Object.assign(window, {
    resetRxDB,
    resetAllLocalData,
    deleteAllRxDBDatabases,
  })
}

// Add TypeScript declarations for global functions
declare global {
  interface Window {
    resetRxDB: typeof resetRxDB
    resetAllLocalData: typeof resetAllLocalData
    deleteAllRxDBDatabases: typeof deleteAllRxDBDatabases
  }
}

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {},
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Global error handlers
window.addEventListener('error', (event) => {
  logger.logError(event.error || new Error(event.message), LogCategory.ERROR, {
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
  })
})

window.addEventListener('unhandledrejection', (event) => {
  logger.logError(
    event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
    LogCategory.ERROR_UNHANDLED,
    {
      promise: true,
    }
  )
})

// Render the app. Before mounting, restore any auth-related localStorage
// entries from the IndexedDB backup. This handles the case where Safari
// iPadOS ITP has purged localStorage between sessions but left IndexedDB
// intact, so the user does not need to log in again. Then drop any
// Cognito/oidc-client-ts leftovers (the restore would otherwise revive them).
void (async () => {
  try {
    // Bound the backup restore so a stalled IndexedDB open/read can never block
    // the React mount. In a second concurrent tab the IDB call can hang and
    // never resolve; without this cap, render() below is never reached and the
    // page stays blank forever (no error, no spinner — React never mounts).
    // Boot must never wait on the backup, so fall through after 1.5s.
    await Promise.race([
      restoreFromIndexedDB().then(() => cleanupLegacyAuthStorage()),
      new Promise<void>((resolve) => setTimeout(resolve, 1500)),
    ])
  } catch (err) {
    logger.logError(
      err instanceof Error ? err : new Error(String(err)),
      LogCategory.ERROR,
      { context: 'authTokenBackup.restoreFromIndexedDB' },
    )
  }

  const rootElement = document.getElementById('app')
  if (rootElement && !rootElement.innerHTML) {
    const root = ReactDOM.createRoot(rootElement)
    root.render(
      <StrictMode>
        <ErrorBoundary>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <RouterProvider router={router} />
            </AuthProvider>
          </QueryClientProvider>
        </ErrorBoundary>
      </StrictMode>,
    )
  }
})()

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
